import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Columns2, Check, RotateCcw, Archive, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { CATEGORY_LABELS } from './SceneQcPanel';

const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const TONE = { fail: 'text-red-700', borderline: 'text-amber-700', pass: 'text-green-700' };

const Pane = ({ label, url, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <p className="text-xs font-medium text-neutral-700 truncate">{label}</p>
    {url ? <a href={abs(url)} target="_blank" rel="noreferrer"><img src={abs(url)} alt={label} className="w-full aspect-square object-cover rounded-md border border-neutral-200" /></a>
      : <div className="w-full aspect-square rounded-md border border-dashed border-neutral-300 flex items-center justify-center text-xs text-neutral-400">No live scene</div>}
  </div>
);

/* Manual Review Queue, one scene at a time: New vs approved style reference vs current live */
export const QueueSideBySide = ({ api, headers, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [refs, setRefs] = useState({});
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [q, r] = await Promise.all([
        axios.get(api, { headers, params: { qc: 'needs_review', status: 'pending', room: 'penthouse_living', limit: 200 } }),
        axios.get(`${api}/qc/references`, { headers }),
      ]);
      setItems(q.data.items); setRefs(r.data); setI(0);
    } catch { toast.error('Could not load the review queue'); }
  }, [api, headers]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const item = items?.[i];
  const act = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${api}/${item.id}/${action}`, {}, { headers });
      toast.success(action === 'approve' ? 'Approved — now live' : action === 'redo' ? 'Regenerating — new version will be auto-QC’d' : 'Kept the existing scene');
      setItems(list => list.filter(x => x.id !== item.id));
      setI(n => Math.max(0, Math.min(n, (items?.length || 1) - 2)));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" data-testid="queue-side-by-side-btn"><Columns2 className="w-3 h-3" />Review side-by-side</Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Manual Review Queue · side-by-side</DialogTitle></DialogHeader>
        {!items ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
          : !item ? <p className="py-12 text-center text-sm text-neutral-600" data-testid="queue-empty">Queue is clear — no Borderline, Fail or QC-wrong scenes left.</p>
            : (
              <div className="space-y-3" data-testid="queue-item">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-neutral-900">{item.color_name} · {item.collection_name}</span>
                  <span className="capitalize text-neutral-500">{item.style}</span>
                  <span className="ml-auto text-neutral-500" data-testid="queue-progress">{i + 1} / {items.length}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={i === 0} onClick={() => setI(n => n - 1)} data-testid="queue-prev">Prev</Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={i >= items.length - 1} onClick={() => setI(n => n + 1)} data-testid="queue-next">Next</Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Pane label="New (pending)" url={item.url} testId="queue-pane-new" />
                  <Pane label={`Approved ${item.style} reference`} url={refs[item.style]} testId="queue-pane-reference" />
                  <Pane label="Live now" url={item.live_url} testId="queue-pane-live" />
                </div>
                <p className={`text-xs ${TONE[item.qc?.verdict] || ''}`} data-testid="queue-qc">
                  <b className="uppercase">QC {item.qc?.verdict}</b>{item.qc?.category ? ` · ${CATEGORY_LABELS[item.qc.category] || item.qc.category}` : ''}{item.qc?.flagged_wrong ? ' · flagged QC wrong' : ''} — {item.qc?.reason}
                </p>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" className="gap-1 bg-green-700 hover:bg-green-800" disabled={busy} onClick={() => act('approve')} data-testid="queue-approve"><Check className="w-3.5 h-3.5" />Approve</Button>
                  <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => act('redo')} data-testid="queue-redo"><RotateCcw className="w-3.5 h-3.5" />Redo</Button>
                  <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => act('keep')} data-testid="queue-keep"><Archive className="w-3.5 h-3.5" />Keep existing</Button>
                </div>
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
};
